import React, { useEffect, useMemo, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import ThemeAwareSpinner from "../../../customComponents/ThemeAwareSpinner";
import "../FundingAccountPage/fundingAccount.css";
import "./futures_trade_extras.css";
import { decNum, sanitizeIncrementInput } from "./futuresIncrementHelpers";
import {
    formatFuturesSymbolMarginError,
    formatMarginModeLabel,
} from "./futuresSymbolMarginHelpers";

const USDT_STEP = "0.00000001";
const AMOUNT_DP = 8;

function formatUsdt(n) {
    const x = decNum(n);
    if (!Number.isFinite(x)) return "—";
    return `${parseFloat(x.toFixed(AMOUNT_DP)).toString()} USDT`;
}

function formatPrice(n) {
    const x = decNum(n);
    if (!Number.isFinite(x) || x <= 0) return "—";
    return `${parseFloat(x.toFixed(AMOUNT_DP)).toLocaleString("en-US", {
        maximumFractionDigits: AMOUNT_DP,
        minimumFractionDigits: 0,
    })} USDT`;
}

function formatPairLabel(pos) {
    const base = pos?.base_currency || "";
    const quote = pos?.quote_currency || pos?.margin_asset || "USDT";
    if (base && quote) return `${base}/${quote}`;
    const sym = String(pos?.symbol || "");
    if (sym.includes("/")) return sym;
    const cleaned = sym.replace(/-PERP$/i, "");
    if (quote && cleaned.toUpperCase().endsWith(String(quote).toUpperCase())) {
        return `${cleaned.slice(0, -quote.length)}/${quote}`;
    }
    return cleaned || "—";
}

/** Isolated liq moves away from mark when margin is added (`± amount / qty`). */
function estimateLiqAfterChange({ currentLiq, qty, side, amount, type }) {
    const liq = decNum(currentLiq);
    const q = decNum(qty);
    const a = decNum(amount);
    if (!Number.isFinite(liq) || liq <= 0) return null;
    if (!Number.isFinite(q) || q <= 0 || !Number.isFinite(a) || a <= 0) return liq;
    const signed = type === "remove" ? -a : a;
    const isLong = String(side || "").toUpperCase() === "LONG";
    const next = isLong ? liq - signed / q : liq + signed / q;
    return Number.isFinite(next) && next > 0 ? next : 0;
}

function clampAmount(raw, max) {
    const n = decNum(raw);
    if (!Number.isFinite(n) || n <= 0) return "";
    const cap = Number.isFinite(max) && max > 0 ? Math.min(n, max) : n;
    return parseFloat(cap.toFixed(AMOUNT_DP)).toString();
}

/**
 * Isolated position — Add / Remove margin (GET preview + POST /isolated-margin).
 * Styled like the futures close-position modal.
 */
export default function FuturesAdjustIsolatedMarginModal({ pos, onClose }) {
    const [tab, setTab] = useState("add");
    const [amount, setAmount] = useState("");
    const [preview, setPreview] = useState(null);
    const [liqPrice, setLiqPrice] = useState(pos?.liquidation_price);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const symbol = pos?.symbol;

    useEffect(() => {
        if (!pos) return undefined;
        setTab("add");
        setAmount("");
        setPreview(null);
        setLiqPrice(pos.liquidation_price);

        let cancelled = false;
        (async () => {
            setLoadingPreview(true);
            try {
                const res = await AuthService.futuresIsolatedMarginPreview(pos.symbol);
                if (cancelled) return;
                if (!res?.success) {
                    const code = String(res?.error?.code || "").toUpperCase();
                    alertErrorMessage(
                        formatFuturesSymbolMarginError(res, "Failed to load margin preview")
                    );
                    if (code === "NO_OPEN_POSITION" || code === "NOT_ISOLATED") onClose?.();
                    return;
                }
                setPreview(res.data || null);
            } catch {
                if (!cancelled) alertErrorMessage("Failed to load margin preview");
            } finally {
                if (!cancelled) setLoadingPreview(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once per open position
    }, [pos?._id, pos?.position_id, pos?.symbol]);

    useEffect(() => {
        if (!pos) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape" && !submitting) onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [pos, onClose, submitting]);

    const quote = pos?.margin_asset || pos?.quote_currency || "USDT";
    const pairLabel = formatPairLabel(pos);
    const isLong = String(pos?.side || "").toUpperCase() === "LONG";
    const sideLabel = isLong ? "Long" : String(pos?.side || "").toUpperCase() === "SHORT" ? "Short" : "—";

    const currentMargin = preview?.isolated_margin ?? pos?.initial_margin;
    const maxAddable = decNum(preview?.max_addable);
    const maxRemovable = decNum(preview?.max_removable);
    const maxForTab = tab === "add" ? maxAddable : maxRemovable;
    const canTab =
        tab === "add"
            ? preview?.can_add !== false && Number.isFinite(maxAddable) && maxAddable > 0
            : preview?.can_remove !== false && Number.isFinite(maxRemovable) && maxRemovable > 0;

    const amountNum = decNum(amount);
    const canSubmit =
        !loadingPreview &&
        !submitting &&
        canTab &&
        Number.isFinite(amountNum) &&
        amountNum > 0 &&
        (!Number.isFinite(maxForTab) || amountNum <= maxForTab + 1e-12);

    const estLiq = useMemo(
        () =>
            estimateLiqAfterChange({
                currentLiq: liqPrice ?? pos?.liquidation_price,
                qty: pos?.quantity,
                side: pos?.side,
                amount,
                type: tab,
            }),
        [liqPrice, pos?.liquidation_price, pos?.quantity, pos?.side, amount, tab]
    );

    const applyMax = () => {
        if (!Number.isFinite(maxForTab) || maxForTab <= 0) {
            setAmount("");
            return;
        }
        setAmount(parseFloat(maxForTab.toFixed(AMOUNT_DP)).toString());
    };

    const switchTab = (next) => {
        if (submitting || next === tab) return;
        setTab(next);
        setAmount("");
    };

    const handleConfirm = async () => {
        if (!canSubmit || !symbol) return;
        const capped = clampAmount(amount, maxForTab);
        if (!capped) return;
        setSubmitting(true);
        try {
            const res = await AuthService.futuresIsolatedMargin({
                symbol,
                type: tab,
                amount: capped,
            });
            if (!res?.success) {
                const code = String(res?.error?.code || "").toUpperCase();
                if (code === "INSUFFICIENT_FUTURES_BALANCE") {
                    alertErrorMessage("Amount exceeds max addable");
                } else if (code === "ISOLATED_MARGIN_TOO_LOW") {
                    alertErrorMessage("Amount exceeds max removable");
                } else {
                    alertErrorMessage(
                        formatFuturesSymbolMarginError(res, "Failed to adjust isolated margin")
                    );
                }
                if (code === "NO_OPEN_POSITION" || code === "NOT_ISOLATED") onClose?.();
                return;
            }
            if (res.data) setPreview(res.data);
            const nextLiq = estimateLiqAfterChange({
                currentLiq: liqPrice ?? pos?.liquidation_price,
                qty: pos?.quantity,
                side: pos?.side,
                amount: capped,
                type: tab,
            });
            if (nextLiq != null) setLiqPrice(nextLiq);
            setAmount("");
            alertSuccessMessage(tab === "add" ? "Margin added" : "Margin removed");
        } catch {
            alertErrorMessage("Failed to adjust isolated margin");
        } finally {
            setSubmitting(false);
        }
    };

    if (!pos) return null;

    return (
        <>
            <div
                className="modal-backdrop fade show"
                onClick={() => !submitting && onClose?.()}
                aria-hidden
            />
            <div
                className="modal fade kyc_modal margin-modal-container show d-block"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                style={{ zIndex: 1055 }}
            >
                <div
                    className="modal-dialog modal-dialog-centered close-position-modal-dialog agce-ft-close-modal-dialog"
                    role="document"
                >
                    <div className="modal-content close-position-modal-content agce-ft-close-modal-content">
                        <div className="modal-header border-0 pb-1 px-4 pt-4">
                            <div>
                                <h5 className="modal-title fw-bold close-position-modal-title mb-1">
                                    Adjust margin
                                </h5>
                                <div className="d-flex align-items-center gap-2 flex-wrap close-position-modal-subtitle">
                                    <span className="fw-semibold text-body">{pairLabel}</span>
                                    <span aria-hidden>·</span>
                                    <span>{formatMarginModeLabel(pos.margin_mode)}</span>
                                    <span
                                        className={`agce-ft-close-modal-side-pill ${
                                            isLong
                                                ? "agce-ft-close-modal-side-pill--long"
                                                : "agce-ft-close-modal-side-pill--short"
                                        }`}
                                    >
                                        {sideLabel}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close margin-modal-close"
                                aria-label="Close"
                                disabled={submitting}
                                onClick={onClose}
                            />
                        </div>

                        <div className="modal-body px-4 pt-3 pb-0 modern-margin-form">
                            <div className="agce-ft-close-modal-tabs" role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === "add"}
                                    className={`agce-ft-close-modal-tab${tab === "add" ? " is-active" : ""}`}
                                    onClick={() => switchTab("add")}
                                    disabled={submitting}
                                >
                                    Add Margin
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === "remove"}
                                    className={`agce-ft-close-modal-tab${
                                        tab === "remove" ? " is-active" : ""
                                    }`}
                                    onClick={() => switchTab("remove")}
                                    disabled={submitting}
                                >
                                    Remove Margin
                                </button>
                            </div>

                            {loadingPreview ? (
                                <div className="d-flex justify-content-center py-4">
                                    <ThemeAwareSpinner size="sm" />
                                </div>
                            ) : (
                                <>
                                    <label className="repay-label">Amount ({quote})</label>
                                    <div className="crypto_selectcoin crypto_selectcoin-2 mb-1">
                                        <div className="price_max_total">
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                autoComplete="off"
                                                placeholder="Enter amount"
                                                value={amount}
                                                disabled={submitting || !canTab}
                                                onChange={(e) =>
                                                    setAmount(
                                                        sanitizeIncrementInput(e.target.value, USDT_STEP)
                                                    )
                                                }
                                                onBlur={() =>
                                                    setAmount(clampAmount(amount, maxForTab))
                                                }
                                                aria-label="Margin amount"
                                            />
                                            <div className="d-flex gap-0 align-items-center">
                                                {quote}
                                                <button
                                                    type="button"
                                                    disabled={submitting || !canTab}
                                                    onClick={applyMax}
                                                >
                                                    Max
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="agce-ft-adj-margin-hint">
                                        {tab === "add"
                                            ? `Max Available ${formatUsdt(preview?.max_addable)}`
                                            : `Max Removable ${formatUsdt(preview?.max_removable)}`}
                                    </p>

                                    <div className="margin-info-rows mb-2">
                                        <div className="margin-info-row">
                                            <span>Currently Assigned Margin</span>
                                            <span>{formatUsdt(currentMargin)}</span>
                                        </div>
                                        <div className="margin-info-row">
                                            <span>
                                                {tab === "add" ? "Max addable" : "Max removable"}
                                            </span>
                                            <span>
                                                {formatUsdt(
                                                    tab === "add"
                                                        ? preview?.max_addable
                                                        : preview?.max_removable
                                                )}
                                            </span>
                                        </div>
                                        <div className="margin-info-row">
                                            <span>Margin Ratio</span>
                                            <span>
                                                {preview?.margin_ratio != null
                                                    ? `${parseFloat(Number(preview.margin_ratio).toFixed(4))}%`
                                                    : "—"}
                                            </span>
                                        </div>
                                        <div className="margin-info-row">
                                            <span>
                                                {tab === "add"
                                                    ? "Est. Liq. Price after increase"
                                                    : "Est. Liq. Price after reduction"}
                                            </span>
                                            <span>{formatPrice(estLiq)}</span>
                                        </div>
                                    </div>

                                    <div className="margin-modal-note mb-3">
                                        <i className="ri-information-fill" />
                                        <span>
                                            {tab === "add"
                                                ? "Adds USDT from your futures wallet into this Isolated position."
                                                : "Returns Isolated margin to your futures wallet. Removable keeps the current-mark ratio at or below 80%."}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="modal-footer border-0 px-4 pb-4 pt-1">
                            <button
                                type="button"
                                className="btn w-100 close-position-confirm-btn agce-ft-close-modal-confirm"
                                onClick={handleConfirm}
                                disabled={!canSubmit}
                            >
                                {submitting ? (
                                    <>
                                        <ThemeAwareSpinner
                                            as="span"
                                            size="sm"
                                            className="me-2"
                                            aria-hidden="true"
                                        />
                                        Applying…
                                    </>
                                ) : (
                                    "Confirm"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
